/**
 * Which of the three layouts this window is currently in.
 *
 * ## Why this exists
 *
 * `useDeviceProfile()` answers "television or not" and is fixed for the life of the page.
 * That is correct for its job — the whole tree must not swap renderers under a running
 * player — but it left the app with no way to tell a phone from a desktop at all. The one
 * place that needed to know read `window.matchMedia('(max-width: 820px)').matches` once,
 * inline, at `App.tsx`, so it was both non-reactive and a fourth copy of a breakpoint.
 *
 * ## Reactive, unlike `useDeviceProfile`
 *
 * Rotating a phone, splitting a window, or docking a tablet all change the answer, and a
 * value sampled at mount would be wrong until the next navigation. `matchMedia` change
 * events rather than a `resize` listener: the browser fires them only when the answer
 * actually crosses a boundary, so a drag from 1200px to 1100px costs nothing.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`, so the first render already
 * has the right value. The effect version renders `desktop` once and then corrects itself,
 * which on a phone is a visible flash of the wrong layout.
 */
import { useSyncExternalStore } from 'react';
import { BREAKPOINTS, viewportFor, type Viewport } from './breakpoints';

/**
 * One query per boundary, built from the same table the stylesheet mirrors.
 *
 * `max-width` uses `.98` rather than a whole pixel: a viewport can be fractional on a
 * scaled display, and `max-width: 640px` plus `min-width: 641px` leaves 640.5 belonging to
 * neither.
 */
const QUERIES = [`(max-width: ${BREAKPOINTS.sm}.98px)`, `(max-width: ${BREAKPOINTS.md}.98px)`];

function read(): Viewport {
  if (typeof window === 'undefined') return 'desktop';
  return viewportFor(window.innerWidth);
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};

  const lists = QUERIES.map((q) => window.matchMedia(q));
  for (const list of lists) list.addEventListener('change', onChange);
  return () => {
    for (const list of lists) list.removeEventListener('change', onChange);
  };
}

/**
 * Cached so `getSnapshot` returns a stable value.
 *
 * `useSyncExternalStore` calls the snapshot on every render and compares by identity. A
 * string is compared by value, so this is safety against a future change to an object
 * shape rather than a present need — but it also means the width is read once per change
 * instead of once per render, and `innerWidth` forces layout.
 */
let cached: Viewport | null = null;

function getSnapshot(): Viewport {
  cached ??= read();
  return cached;
}

function subscribeAndInvalidate(onChange: () => void): () => void {
  return subscribe(() => {
    cached = read();
    onChange();
  });
}

/** Server render and the first paint of a packaged widget both land on `desktop`. */
function getServerSnapshot(): Viewport {
  return 'desktop';
}

export function useViewport(): Viewport {
  return useSyncExternalStore(subscribeAndInvalidate, getSnapshot, getServerSnapshot);
}

/** `useViewport() === 'phone'`, for the common case. */
export function useIsPhone(): boolean {
  return useViewport() === 'phone';
}
